import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, Code2, FlaskConical, BrainCircuit, ArrowRight, ArrowLeft, Phone, Mail, UserCircle2,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import { useI18n } from "@/i18n/I18nContext";
import { PAGES } from "@/i18n/pages";
import { TECH_CONTACT } from "@/i18n/extras";

const CATEGORY_ICONS = {
  cyber: ShieldCheck,
  software: Code2,
  qa: FlaskConical,
  ai: BrainCircuit,
};

const HERO_IMG =
  "https://images.unsplash.com/photo-1518770660439-4636190af475?fm=jpg&q=80&w=2000&auto=format&fit=crop";

const TechnologyPage = () => {
  const { locale } = useI18n();
  const tr = PAGES[locale]?.technology || PAGES.en.technology;
  const tc = TECH_CONTACT[locale] || TECH_CONTACT.en;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="technology-page">
      <Navbar />
      <main>
        <section className="relative overflow-hidden bg-[hsl(var(--tbs-navy-950))] text-white">
          <div className="absolute inset-0 opacity-10 pointer-events-none"
            style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
          <div className="absolute -top-32 -right-24 h-96 w-96 rounded-full bg-[hsl(var(--accent))]/15 blur-3xl pointer-events-none" />

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 md:pt-20 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-7">
              <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.14em] uppercase text-white/70 hover:text-[hsl(var(--accent))]">
                <ArrowLeft className="h-3.5 w-3.5" /> Home
              </Link>
              <div className="mt-3 text-[11px] font-semibold tracking-[0.18em] text-[hsl(var(--accent))] uppercase">{tr.eyebrow}</div>
              <h1 className="mt-3 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]" data-testid="technology-hero-title">
                {tr.hero.title}
              </h1>
              <p className="mt-5 max-w-2xl text-base md:text-lg leading-relaxed text-white/80">{tr.hero.subtitle}</p>
              <div className="mt-7">
                <Button asChild size="lg" className="h-12 px-6 rounded-xl bg-[hsl(var(--accent))] hover:bg-[hsl(var(--tbs-gold-600))] text-[hsl(var(--primary))] font-semibold">
                  <Link to="/#contact">{tr.hero.cta}<ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 -z-10 rounded-[28px] bg-[hsl(var(--accent))]/15 blur-2xl" />
                <div className="overflow-hidden rounded-3xl border border-white/10 aspect-[4/5]">
                  <img src={HERO_IMG} alt="Technology" className="w-full h-full object-cover" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {tr.categories.map((cat, idx) => {
          const Icon = CATEGORY_ICONS[cat.key] || Code2;
          const dark = idx % 2 === 1;
          return (
            <section key={cat.key} className={`py-20 md:py-24 ${dark ? "bg-[hsl(var(--tbs-cream-50))]" : "bg-[hsl(var(--background))]"}`} data-testid={`tech-category-${cat.key}`}>
              <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-start gap-4 max-w-3xl">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[hsl(var(--primary))] text-white shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="font-display text-3xl md:text-4xl text-[hsl(var(--primary))]">{cat.title}</h2>
                    <p className="mt-2 text-slate-700">{cat.lead}</p>
                  </div>
                </div>
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cat.items.map((it, i) => (
                    <motion.div key={it.title} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.4, delay: i * 0.03 }}>
                      <Card className="card-lift h-full bg-white border border-slate-200 rounded-2xl p-5">
                        <div className="flex items-start gap-2.5">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] shrink-0" />
                          <div>
                            <h3 className="font-semibold text-[hsl(var(--primary))] text-[15px] leading-snug">{it.title}</h3>
                            <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{it.desc}</p>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          );
        })}

        {/* Dedicated tech contact block */}
        <section id="tech-contact" data-testid="tech-contact-section" className="py-20 md:py-24 bg-[hsl(var(--background))]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="text-[11px] font-semibold tracking-[0.18em] text-[hsl(var(--accent))] uppercase">
                {tc.eyebrow}
              </div>
              <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl leading-[1.08] text-[hsl(var(--primary))]">
                {tc.title}
              </h2>
              <p className="mt-4 text-slate-700 leading-relaxed">{tc.lead}</p>
            </div>

            <Card
              data-testid="tech-contact-card"
              className="mt-10 bg-[hsl(var(--primary))] text-white border-0 rounded-3xl p-8 md:p-10 relative overflow-hidden"
            >
              <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[hsl(var(--accent))]/15" />
              <div className="absolute -left-16 -bottom-16 h-56 w-56 rounded-full bg-[hsl(var(--accent))]/10" />
              <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                <div className="md:col-span-4 flex items-start gap-4">
                  <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[hsl(var(--accent))] text-[hsl(var(--primary))] shrink-0">
                    <UserCircle2 className="h-8 w-8" />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] text-[hsl(var(--accent))] uppercase">
                      {tc.roleTag}
                    </div>
                    <div className="mt-1 font-display text-2xl">{tc.personName}</div>
                  </div>
                </div>

                <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <a
                    href={`tel:${tc.phone.replace(/\s+/g, "")}`}
                    data-testid="tech-contact-phone"
                    className="group bg-white/5 hover:bg-white/10 rounded-2xl p-4 border border-white/10 transition-colors"
                  >
                    <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.14em] text-[hsl(var(--accent))] uppercase">
                      <Phone className="h-3.5 w-3.5" /> {tc.phoneLabel}
                    </div>
                    <div className="mt-1.5 text-lg font-semibold tabular-nums group-hover:text-[hsl(var(--accent))] transition-colors">
                      {tc.phone}
                    </div>
                  </a>
                  <a
                    href={`mailto:${tc.email}`}
                    data-testid="tech-contact-email"
                    className="group bg-white/5 hover:bg-white/10 rounded-2xl p-4 border border-white/10 transition-colors"
                  >
                    <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.14em] text-[hsl(var(--accent))] uppercase">
                      <Mail className="h-3.5 w-3.5" /> {tc.emailLabel}
                    </div>
                    <div className="mt-1.5 text-sm font-semibold break-all group-hover:text-[hsl(var(--accent))] transition-colors">
                      {tc.email}
                    </div>
                  </a>
                </div>
              </div>

              <div className="relative mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6 border-t border-white/10">
                <span className="text-xs text-white/65">{tc.note}</span>
                <div className="flex gap-3">
                  <Button
                    asChild
                    className="rounded-xl bg-white/10 hover:bg-white/20 text-white border border-white/20"
                  >
                    <a href={`tel:${tc.phone.replace(/\s+/g, "")}`}>
                      <Phone className="mr-2 h-4 w-4" />
                      {tc.callCta}
                    </a>
                  </Button>
                  <Button
                    asChild
                    className="rounded-xl bg-[hsl(var(--accent))] hover:bg-[hsl(var(--tbs-gold-600))] text-[hsl(var(--primary))] font-semibold"
                  >
                    <a href={`mailto:${tc.email}`}>
                      <Mail className="mr-2 h-4 w-4" />
                      {tc.emailCta}
                    </a>
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-[hsl(var(--background))]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="bg-[hsl(var(--primary))] text-white border-0 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[hsl(var(--accent))]/15" />
              <div className="relative">
                <h3 className="font-display text-3xl md:text-4xl">{tr.cta.title}</h3>
                <p className="mt-3 max-w-2xl mx-auto text-white/80">{tr.cta.body}</p>
                <Button asChild size="lg" className="mt-6 h-12 px-6 rounded-xl bg-[hsl(var(--accent))] hover:bg-[hsl(var(--tbs-gold-600))] text-[hsl(var(--primary))] font-semibold">
                  <Link to="/#contact">{tr.cta.button}<ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              </div>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default TechnologyPage;
